import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { savingsBasisNote } from '@/lib/savingsBasis';
import api from '@/lib/api';
import { TailSupplierTable } from './TailSupplierTable';
import { TailCategoryChart } from './TailCategoryChart';

const fmt = (cs, v) => `${cs}${Math.round(v || 0).toLocaleString()}`;

function Kpi({ title, value, sub, help, testId, accent }) {
  return (
    <Card className={`border-l-4 ${accent}`} data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
          {title} <HelpBubble text={help} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-xl font-bold font-mono">{value}</div>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export function TailSpendTab({ queryString, cs }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setError(false);
    api.get(`/analytics/tail-spend${queryString ? `?${queryString}` : ''}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [queryString]);

  if (error) return <p className="text-sm text-destructive py-8 text-center" data-testid="tail-spend-error">Failed to load tail spend.</p>;
  if (!data) return <p className="text-sm text-muted-foreground py-8 text-center" data-testid="tail-spend-loading">Loading tail spend…</p>;

  const pct = data.threshold_pct;
  return (
    <div className="space-y-4" data-testid="tail-spend-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi title="TAIL SPEND" testId="tail-kpi-spend" accent="border-l-amber-500"
          value={fmt(cs, data.total_tail_spend)}
          help={`Combined spend with suppliers that each hold under ${pct}% of addressable spend in this view.`} />
        <Kpi title="% OF ADDRESSABLE SPEND" testId="tail-kpi-pct" accent="border-l-orange-500"
          value={`${data.tail_spend_percentage}%`}
          help="Tail spend as a share of addressable spend. Above ~15-20% usually signals fragmented, unmanaged buying." />
        <Kpi title="TAIL SUPPLIERS" testId="tail-kpi-suppliers" accent="border-l-sky-500"
          value={data.supplier_count.toLocaleString()} sub={`of ${data.total_supplier_count.toLocaleString()} suppliers`}
          help={`Suppliers each holding under ${pct}% of addressable spend — candidates for consolidation, catalogues or P-cards.`} />
        <Kpi title="POTENTIAL SAVINGS" testId="tail-kpi-savings" accent="border-l-emerald-500"
          value={fmt(cs, data.potential_savings)} sub={`${Math.round(data.savings_rate * 100)}% of tail spend`}
          help="Indicative savings from consolidating the tail onto preferred suppliers or framework agreements. Same rate as the Tail Spend signal." />
      </div>
      <p className="text-xs text-muted-foreground" data-testid="tail-basis-note">{savingsBasisNote(data, cs)}</p>
      {data.supplier_count === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="tail-empty">
          No tail suppliers in this view — every supplier holds at least {pct}% of addressable spend.
        </CardContent></Card>
      ) : (
        <>
          <TailCategoryChart rows={data.tail_by_category} cs={cs} />
          <TailSupplierTable rows={data.tail_suppliers} cs={cs} />
        </>
      )}
    </div>
  );
}
